import { useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  fetchAccountMaxes, setMaxesLock, resetMaxes, bulkArchiveMaxes,
} from '../ducks/maxesGoalsActions';
import { showSuccessToast } from '../../../shared/utils/toastManager';

// The manage menu on maxes & goals and its confirm popup
const useManageMaxes = ({
  accountCode, activeFilter, rows, isAthleteScope, exercisesById, scopeLabel, exerciseLabel,
}) => {
  const dispatch = useDispatch();
  const [pendingManageAction, setPendingManageAction] = useState(null);
  const [isManageBusy, setIsManageBusy] = useState(false);

  // Lock and reset only apply to rows with a current max
  const rowsWithMax = rows.filter((row) => row.value);
  const lockedCount = rowsWithMax.filter((row) => Boolean(row.isLocked)).length;
  const lockExerciseIds = isAthleteScope
    ? rowsWithMax.map((row) => row.exerciseId)
    : [activeFilter.exerciseId].filter(Boolean);
  const archiveExerciseIds = isAthleteScope
    ? rows.map((row) => row.exerciseId)
    : [activeFilter.exerciseId].filter(Boolean);
  const manageScopeText = isAthleteScope
    ? `every exercise for ${scopeLabel}`
    : `${exerciseLabel} for every athlete in ${scopeLabel}`;

  const refreshMaxesTable = () => dispatch(fetchAccountMaxes(accountCode, activeFilter));
  const closeManageConfirm = () => setPendingManageAction(null);

  const finishManageAction = () => {
    setIsManageBusy(false);
    setPendingManageAction(null);
    refreshMaxesTable();
  };

  const handleLockAll = (isLocking) => {
    dispatch(setMaxesLock(accountCode, {
      filterType: activeFilter.filterType,
      filterIds: activeFilter.filterIds,
      exerciseIds: lockExerciseIds,
    }, isLocking, {
      onSuccess: () => {
        showSuccessToast(isLocking ? 'Current maxes locked.' : 'Current maxes unlocked.');
        refreshMaxesTable();
      },
    }));
  };

  // Athlete scope resets each exercise type in the table - others reset the picked exercise
  const handleResetAll = () => {
    const filterId = activeFilter.filterIds?.[0] ?? null;
    const requests = isAthleteScope
      ? [...new Set(rowsWithMax.map((row) => exercisesById[row.exerciseId]?.type).filter(Boolean))]
        .map((exerciseType) => ({ filterType: activeFilter.filterType, filterId, exerciseType }))
      : [{
        filterType: activeFilter.filterType,
        filterId,
        exerciseType: exercisesById[activeFilter.exerciseId]?.type,
        exerciseId: activeFilter.exerciseId,
      }];
    setIsManageBusy(true);
    Promise.all(requests.map((body) => dispatch(resetMaxes(accountCode, body))))
      .then(finishManageAction);
  };

  // One request per exercise type - an empty list would delete every exercise of that type
  const handleArchiveAll = () => {
    const idsByType = archiveExerciseIds.reduce((acc, id) => {
      const type = exercisesById[id]?.type;
      if (!type) return acc;
      acc[type] = [...(acc[type] || []), id];
      return acc;
    }, {});
    setIsManageBusy(true);
    Promise.all(Object.entries(idsByType).map(([exerciseType, exerciseIds]) => (
      dispatch(bulkArchiveMaxes(accountCode, {
        filterType: activeFilter.filterType,
        filterIds: activeFilter.filterIds,
        exerciseType,
        exerciseIds,
      }))
    ))).then(finishManageAction);
  };

  const manageItems = [
    {
      key: 'lock',
      icon: 'lock',
      label: 'Lock all',
      disabled: lockExerciseIds.length === 0 || lockedCount === rowsWithMax.length,
      disabledReason: rowsWithMax.length === 0
        ? 'No current maxes to lock'
        : 'All current maxes are already locked',
      onClick: () => handleLockAll(true),
    },
    {
      key: 'unlock',
      icon: 'key',
      label: 'Unlock all',
      disabled: lockExerciseIds.length === 0 || lockedCount === 0,
      disabledReason: 'No locked maxes',
      onClick: () => handleLockAll(false),
    },
    { key: 'divider-1', divider: true },
    {
      key: 'reset',
      icon: 'restore',
      label: 'Reset current maxes',
      iconColor: '#FF0505',
      disabled: rowsWithMax.length === 0,
      disabledReason: 'No current maxes to reset',
      onClick: () => setPendingManageAction('reset'),
    },
    { key: 'divider-2', divider: true },
    {
      key: 'archive-all',
      icon: 'delete',
      label: 'Delete all',
      iconColor: '#FF0505',
      disabled: archiveExerciseIds.length === 0 || rows.length === 0,
      disabledReason: 'No maxes to delete',
      onClick: () => setPendingManageAction('archiveAll'),
    },
  ];

  const manageConfirmCopy = {
    reset: {
      title: 'Reset Current Maxes',
      message: `This clears ${rowsWithMax.length} current max${rowsWithMax.length === 1 ? '' : 'es'} (${manageScopeText}) and unlocks them. History is kept, but there will be no current max until a new one is set or entered.`,
      confirmLabel: 'Reset',
      onConfirm: handleResetAll,
    },
    archiveAll: {
      title: 'Delete All Maxes',
      message: `Delete every recorded max for ${manageScopeText}? Locked maxes are deleted too, and they will be removed from history.`,
      confirmLabel: 'Delete all',
      onConfirm: handleArchiveAll,
    },
  }[pendingManageAction] || {};

  return {
    manageItems,
    manageConfirmProps: {
      isOpen: Boolean(pendingManageAction),
      title: manageConfirmCopy.title,
      message: manageConfirmCopy.message,
      confirmLabel: manageConfirmCopy.confirmLabel,
      isBusy: isManageBusy,
      onConfirm: manageConfirmCopy.onConfirm || closeManageConfirm,
      onClose: closeManageConfirm,
    },
  };
};

export default useManageMaxes;
